import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Button from 'reactstrap/lib/Button';
import { Disclaimer } from 'site-modules/shared/components/native-ad/native-ad-components/disclaimer/disclaimer';
import { getImageUrl, SOURCES } from 'client/utils/image-helpers';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { getRelAttribute } from 'site-modules/shared/components/native-ad/utils/get-rel-attribute';
import './high-impact-content.scss';

export function HighImpactContentSlide({ slideContent, position = undefined, isActive, slideId }) {
  const focusRef = useRef();
  const prevActiveRef = useRef(isActive);

  useEffect(() => {
    if (isActive && isActive !== prevActiveRef.current) {
      focusRef.current.focus();
    }
    prevActiveRef.current = isActive;
  }, [isActive]);

  const { image, url, urlText, urlTargetNew, verbiage, subtitle, oemLink, adLabel, disclaimer } = slideContent;

  const isTargetNew = urlTargetNew === 'true';
  const isSponsored = adLabel === 'Ad';
  const rel = getRelAttribute(isSponsored, isTargetNew);
  const vehicleImage = getImageUrl(image, null, SOURCES.STATIC);

  return (
    <div
      className="high-impact-content-slide text-white w-100 mx-0 mx-lg-auto pos-r d-flex"
      style={{ backgroundImage: `url("${vehicleImage}")` }}
      role="tabpanel"
      aria-labelledby={`tab-${slideId}`}
      id={`tabpanel-${slideId}`}
      ref={focusRef}
    >
      <div className="gradient-layer pos-a top-0 start-0 w-100 h-100 rounded-8" aria-hidden="true" />
      <div
        className={classNames('high-impact-content-slide-text mt-auto ms-3', {
          'mb-3_5': !disclaimer,
          'mb-2': disclaimer,
        })}
        style={{
          maxWidth: '307px',
        }}
      >
        {adLabel && (
          <div
            className="ad-bug text-center fw-medium"
            style={{
              width: '49px',
              height: '24px',
              lineHeight: '22px',
              border: '1px solid #475569',
              fontSize: '0.75rem',
            }}
            aria-hidden
          >
            AD
          </div>
        )}
        <ContentFragment classes="heading-4 text-white mt-1 fw-medium text-shadow-contrast" componentToUse="h2">
          {verbiage}
        </ContentFragment>
        {subtitle && (
          <ContentFragment
            classes="fw-bold text-white text-shadow-contrast"
            inlineStyle={{
              fontSize: '3rem',
              lineHeight: '3.25rem',
            }}
          >
            {subtitle}
          </ContentFragment>
        )}
        <div className="pt-1 pb-0">
          <Button
            outline
            className="high-impact-cta text-capitalize px-1 py-0_75"
            href={encodeURI(url)}
            target={isTargetNew ? '_blank' : '_self'}
            rel={rel}
            data-tracking-id={isTargetNew ? 'high_impact_click_out' : 'high_impact_click_in'}
            data-tracking-value={`main_${urlText}`}
            data-tracking-position={position}
            aria-label={`${urlText} about ${verbiage}`}
            tabIndex={!isActive ? -1 : 0}
          >
            <div className="fw-medium high-impact-cta-text text-cool-gray-10 d-flex align-items-center">
              {urlText}
              {isTargetNew && (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width={10}
                  height={11}
                  fill="none"
                  transform="scale(0.835)"
                  className="ms-0_5"
                  aria-hidden
                >
                  <path
                    fill="#0F1A2A"
                    stroke="#0F1A2A"
                    strokeWidth=".5"
                    d="m8.4 1.083.15.014a.723.723 0 0 1 .383.203l.096.116a.742.742 0 0 1 .12.417v7a.739.739 0 0 1-.753.75.737.737 0 0 1-.747-.75V3.637l-6.09 6.09a.729.729 0 0 1-.526.223.729.729 0 0 1-.526-.223.731.731 0 0 1 0-1.054l6.09-6.09H1.4a.732.732 0 0 1-.534-.218.738.738 0 0 1-.216-.535.737.737 0 0 1 .75-.747h7Z"
                  />
                </svg>
              )}
            </div>
          </Button>
          <span className="heading-7 text-white fw-normal ps-1">{oemLink}</span>
        </div>
        {disclaimer && (
          <div
            className={classNames('disclaimer-wrapper text-start', {
              'ps-2': isTargetNew,
              'ps-1_5': !isTargetNew,
            })}
          >
            <Disclaimer
              linkDisclaimer="Disclaimer*"
              disclaimerText={disclaimer}
              contentClasses="legal pt-2"
              data-tracking-parent="edm-entry-home-high-impact"
              openTrackingId="high_impact_disclaimer_click"
              closeTrackingId="high_impact_disclaimer_close"
              buttonClasses="text-white"
              tabIndex={!isActive ? -1 : 0}
            />
          </div>
        )}
      </div>
    </div>
  );
}

HighImpactContentSlide.propTypes = {
  slideContent: PropTypes.shape({
    image: PropTypes.string,
    url: PropTypes.string,
    urlText: PropTypes.string,
    urlTargetNew: PropTypes.string,
    verbiage: PropTypes.string,
    subtitle: PropTypes.string,
    oemLink: PropTypes.string,
    adLabel: PropTypes.string,
  }).isRequired,
  position: PropTypes.string,
  isActive: PropTypes.bool.isRequired,
  slideId: PropTypes.string.isRequired,
};

export function HighImpactContent({ slides, selectedSlideIndex = 0 }) {
  const renderedSlides = slides.map((slideItem, index) => {
    if (!slideItem) {
      return false;
    }
    return (
      <HighImpactContentSlide
        key={slideItem.id}
        slideContent={slideItem.getAllMetadata()}
        position={`${index + 1}`}
        isActive={selectedSlideIndex === index}
        slideId={slideItem.id}
      />
    );
  });

  return (
    <div
      className="overflow-hidden slides-view-box"
      style={{
        height: '476px',
      }}
    >
      <div
        className="d-flex flex-column slides-container"
        style={{
          height: `${slides.length * 100}%`,
          transform: `translateY(-${selectedSlideIndex * (100 / slides.length).toFixed(2)}%)`,
        }}
      >
        {renderedSlides}
      </div>
    </div>
  );
}

HighImpactContent.propTypes = {
  slides: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  selectedSlideIndex: PropTypes.number,
};
